import _ from 'lodash';

type GroupVsRemainderContent = Pick<
  Studio.ChartContent,
  'visType' | 'description'
> & { line?: Studio.LineOptions };

// The classic editor always saves it; nothing reads it
const LEGACY_LIMIT = 20;

// Whether a Line draws a field for the selection against the rest, saved as
// the legacy Group Vs Remainder
export const isGroupVsRemainderLine = (
  content: GroupVsRemainderContent,
): boolean =>
  content.visType === 'line' && content.line?.mode === 'groupVsRemainder';

// The legacy Group Vs Remainder a Line block in that mode saves as and draws
// with (components/GroupVsRemainder), as the classic editor builds it: a
// y-axis bound is 'set' only when there's a value for it.
const buildGroupVsRemainderDefinition = ({
  card,
  id,
  name,
}: {
  card: GroupVsRemainderContent;
  id: string;
  name: string;
}):
  | { chartDefinition: VisualisationDefinitions.GroupVsRemainder }
  | { missing: string } => {
  const settings = card.line?.groupVsRemainder ?? {};
  if (!settings.dataset || !settings.field) {
    return { missing: 'Choose a dataset and a number field' };
  }
  return {
    chartDefinition: {
      id,
      name,
      description: card.description ?? '',
      type: 'GroupVsRemainder',
      dataset: settings.dataset,
      field: settings.field,
      peerGroup: settings.peerGroup ?? '',
      useEntityAsScope: !!settings.useEntityAsScope,
      trendByCalendarInterval: settings.interval ?? 'auto',
      minYBehaviour: settings.minY === undefined ? 'auto' : 'set',
      maxYBehaviour: settings.maxY === undefined ? 'auto' : 'set',
      ...(settings.minY === undefined ? {} : { minY: settings.minY }),
      ...(settings.maxY === undefined ? {} : { maxY: settings.maxY }),
      limit: LEGACY_LIMIT,
    },
  };
};

// A bound only counts when it's set and has a value
const readBound = ({
  behaviour,
  value,
}: {
  behaviour: 'auto' | 'set';
  value: number | undefined;
}): number | undefined =>
  behaviour === 'set' && typeof value === 'number' ? value : undefined;

// A Group vs remainder Line's settings from a saved Group Vs Remainder
export const guessGroupVsRemainderCard = (
  chartDef: VisualisationDefinitions.GroupVsRemainder,
): Studio.GadgetSidecarCard => ({
  visType: 'line',
  name: chartDef.name,
  ...(chartDef.description ? { description: chartDef.description } : {}),
  line: {
    mode: 'groupVsRemainder',
    groupVsRemainder: _.omitBy(
      {
        dataset: chartDef.dataset,
        field: chartDef.field,
        peerGroup: chartDef.peerGroup || undefined,
        useEntityAsScope: chartDef.useEntityAsScope || undefined,
        interval:
          chartDef.trendByCalendarInterval === 'auto'
            ? undefined
            : chartDef.trendByCalendarInterval,
        minY: readBound({
          behaviour: chartDef.minYBehaviour,
          value: chartDef.minY,
        }),
        maxY: readBound({
          behaviour: chartDef.maxYBehaviour,
          value: chartDef.maxY,
        }),
      },
      _.isUndefined,
    ),
  },
});

// Whether the settings rebuild the saved Group Vs Remainder. Its name is
// the gadget's own, as is what it keeps for soft deletes. Off flags, an
// unused bound, the limit, the selection (written onto it as it draws) and
// a version saved on it change nothing.
export const reproducesGroupVsRemainder = ({
  card,
  chartDef,
}: {
  card: Studio.GadgetSidecarCard;
  chartDef: VisualisationDefinitions.GroupVsRemainder;
}): boolean => {
  if (!isGroupVsRemainderLine(card)) {
    return false;
  }
  const built = buildGroupVsRemainderDefinition({
    card,
    id: chartDef.id,
    name: chartDef.name,
  });
  const comparable = (def: VisualisationDefinitions.GroupVsRemainder) => ({
    ..._.omit(def, [
      'isHidden',
      'hiddenOn',
      'version',
      'limit',
      'selectedEntities',
      'minY',
      'maxY',
      'minYBehaviour',
      'maxYBehaviour',
    ]),
    description: def.description || undefined,
    peerGroup: def.peerGroup || undefined,
    useEntityAsScope: def.useEntityAsScope || undefined,
    trendByCalendarInterval: def.trendByCalendarInterval || 'auto',
    minY: readBound({ behaviour: def.minYBehaviour, value: def.minY }),
    maxY: readBound({ behaviour: def.maxYBehaviour, value: def.maxY }),
  });
  return (
    'chartDefinition' in built &&
    _.isEqual(comparable(built.chartDefinition), comparable(chartDef))
  );
};

export default buildGroupVsRemainderDefinition;
